import React, { useMemo, useState } from "react";

import useSWR from "swr";

import ManageHistoryIcon from "@mui/icons-material/ManageHistory";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { useSort } from "@tzmedical/react-hooks";

import { swrFetcher } from "../axiosClient.js";
import Alert from "./common/Alert.jsx";
import ColumnHeader from "./common/ColumnHeader.jsx";
import DialogTitleBar from "./common/DialogTitleBar.jsx";
import PageLoading from "./common/PageLoading.jsx";
import ReadableCurrency from "./ReadableCurrency.jsx";
import ReadableProjectName from "./ReadableProjectName.jsx";

const sortOptions = {
  defaultSort: {
    field: "hours",
    reverse: true,
  },
};

const issueSortOptions = {
  defaultSort: {
    field: "key",
    reverse: false,
  },
};

const SprintChip = ({ sprint, user }) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const {
    data: unknownIssues,
    error: swrError,
    isLoading: loadingUnknown,
    mutate,
  } = useSWR(
    open ? { url: `/api/jira/sprint/${sprint.id}/unknown`, params: { userId: user.id } } : null,
    swrFetcher
  );

  const error = swrError ? swrError.response?.data?.error || swrError.message : null;
  const setError = React.useCallback(
    (err) => {
      // Replicate old behavior of re-fetching data when "closing" an error message
      if (!err) {
        mutate();
      }
    },
    [mutate]
  );

  const projectsInSprint = useMemo(() => {
    const projectsMap = new Map();
    user.userWork?.forEach((work) => {
      if (work.sprint?.id === sprint.id && work.project) {
        const projectId = work.project.id;
        if (!projectsMap.has(projectId)) {
          projectsMap.set(projectId, {
            id: projectId,
            name: work.project.name,
            jiraId: work.project.jiraId,
            hours: 0,
            cost: 0,
          });
        }
        const projectStats = projectsMap.get(projectId);
        projectStats.hours += Number(work.hours);
        projectStats.cost += Number(work.hours) * Number(user.hourlyRate);
      }
    });
    return Array.from(projectsMap.values());
  }, [sprint.id, user.userWork, user.hourlyRate]);

  const [sortedProjects, handleSortSelection, sort] = useSort(projectsInSprint, sortOptions);

  const flatUnknownIssues = useMemo(
    () =>
      (unknownIssues || []).map((issue) => ({
        key: issue.key,
        assignee: issue.fields.assignee?.displayName || "Unassigned",
        summary: issue.fields.summary,
        epic: issue.fields.parent?.fields.summary || "-",
        labels: issue.fields.labels || [],
        resolutiondate: issue.fields.resolutiondate,
      })),
    [unknownIssues]
  );

  const [sortedUnknownIssues, handleUnknownSortSelection, unknownSort] = useSort(
    flatUnknownIssues,
    issueSortOptions
  );

  return (
    <>
      <Chip label={sprint.name} size="small" variant="outlined" onClick={handleClickOpen} />
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <Alert message={error} setMessage={setError} level="error" />
        <DialogTitleBar title={`Projects in ${sprint.name}`} Icon={ManageHistoryIcon} />
        <DialogContent>
          <Container maxWidth="md">
            <Grid container spacing={1}>
              <Grid item size={6}>
                <ColumnHeader
                  id="name"
                  display="Project"
                  sortField={sort.field}
                  reverseSort={sort.reverse}
                  onClick={handleSortSelection}
                />
              </Grid>
              <Grid item size={3}>
                <ColumnHeader
                  id="hours"
                  display="Hours"
                  sortField={sort.field}
                  reverseSort={sort.reverse}
                  onClick={handleSortSelection}
                />
              </Grid>
              <Grid item size={3}>
                <ColumnHeader
                  id="cost"
                  display="Cost"
                  sortField={sort.field}
                  reverseSort={sort.reverse}
                  onClick={handleSortSelection}
                />
              </Grid>
            </Grid>
            <Divider sx={{ my: 1 }} />
            {projectsInSprint.length === 0 && (
              <Box sx={{ alignItems: "center", pt: 2 }}>
                <Typography align="center" sx={{ fontStyle: "italic" }}>
                  No project information found for this sprint.
                </Typography>
              </Box>
            )}
            {sortedProjects.map((project) => (
              <React.Fragment key={project.id}>
                <Grid container spacing={1} sx={{ alignItems: "center", py: 0.5 }}>
                  <Grid item size={6}>
                    <ReadableProjectName name={project.name} jiraId={project.jiraId} />
                  </Grid>
                  <Grid item size={3}>
                    <Typography>{Math.round(project.hours)}</Typography>
                  </Grid>
                  <Grid item size={3}>
                    <ReadableCurrency value={project.cost} />
                  </Grid>
                </Grid>
                <Divider />
              </React.Fragment>
            ))}
          </Container>

          <Box sx={{ mt: 4, mb: 1 }}>
            <Typography variant="h5">Issues from Unknown Projects</Typography>
          </Box>
          <Grid container spacing={1}>
            <Grid item size={1.5}>
              <ColumnHeader
                id="key"
                display="ID"
                sortField={unknownSort.field}
                reverseSort={unknownSort.reverse}
                onClick={handleUnknownSortSelection}
              />
            </Grid>
            <Grid item size={2}>
              <ColumnHeader
                id="assignee"
                display="Assignee"
                sortField={unknownSort.field}
                reverseSort={unknownSort.reverse}
                onClick={handleUnknownSortSelection}
              />
            </Grid>
            <Grid item size="grow">
              <ColumnHeader
                id="summary"
                display="Summary"
                sortField={unknownSort.field}
                reverseSort={unknownSort.reverse}
                onClick={handleUnknownSortSelection}
              />
            </Grid>
            <Grid item size="grow">
              <ColumnHeader
                id="epic"
                display="Epic"
                sortField={unknownSort.field}
                reverseSort={unknownSort.reverse}
                onClick={handleUnknownSortSelection}
              />
            </Grid>
            <Grid item size={2}>
              <ColumnHeader
                id="labels"
                display="Labels"
                sortField={unknownSort.field}
                reverseSort={unknownSort.reverse}
                onClick={handleUnknownSortSelection}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 1 }} />
          {loadingUnknown && <PageLoading size={100} />}
          {!loadingUnknown && unknownIssues?.length === 0 && (
            <Box sx={{ alignItems: "center", pt: 2 }}>
              <Typography align="center" sx={{ fontStyle: "italic" }}>
                No issues from unknown projects found.
              </Typography>
            </Box>
          )}
          {sortedUnknownIssues.map((issue) => (
            <React.Fragment key={issue.key}>
              <Grid container spacing={1} sx={{ alignItems: "center", py: 0.5 }}>
                <Grid item size={1.5}>
                  <Typography variant="body2">{issue.key}</Typography>
                </Grid>
                <Grid item size={2}>
                  <Typography variant="body2">{issue.assignee}</Typography>
                </Grid>
                <Grid item size="grow">
                  <Typography variant="body2">{issue.summary}</Typography>
                </Grid>
                <Grid item size="grow">
                  <Typography variant="body2">{issue.epic}</Typography>
                </Grid>
                <Grid item size={2}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {issue.labels.map((label) => (
                      <Chip key={label} label={label} size="small" />
                    ))}
                    {issue.labels.length === 0 && "-"}
                  </Box>
                </Grid>
              </Grid>
              <Divider />
            </React.Fragment>
          ))}
        </DialogContent>
        <DialogActions>
          <Box sx={{ m: 2 }}>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SprintChip;
